import DOMPurify from "dompurify";
import { buildAttachmentProxyUrl } from "./fogbugz-attachment";

/**
 * Sanitise a FogBugz event HTML fragment and resolve any relative link
 * URLs against the given FogBugz base URL.
 *
 * Images are preserved only when their `src` resolves to the FogBugz origin
 * and can be rewritten through the authenticated attachment proxy
 * (`/api/v1/fogbugz/attachment-proxy` with `inline=true`), i.e. supported
 * FogBugz attachment download URLs. Cross-origin, `javascript:`, and `data:`
 * URI images, as well as FogBugz-origin images that are not supported
 * attachment URLs, are stripped. When `fogbugzBaseUrl` is null all images are
 * removed (fail-closed).
 *
 * Returns `null` when the sanitised result is empty or whitespace-only,
 * so callers can fall back to `sText` / `sChanges`.
 */
export function sanitizeFogBugzHtml(
  html: string,
  fogbugzBaseUrl?: string | null,
): string | null {
  // DOMPurify: allow common formatting tags + img; strip scripts and event handlers
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "b",
      "i",
      "em",
      "strong",
      "u",
      "s",
      "a",
      "ul",
      "ol",
      "li",
      "blockquote",
      "pre",
      "code",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "div",
      "span",
      "hr",
      "img",
    ],
    ALLOWED_ATTR: ["href", "alt", "title", "width", "height", "target", "src"],
    ALLOW_DATA_ATTR: false,
  });

  if (!clean.trim()) return null;

  // Resolve relative link URLs against the FogBugz origin
  const baseOrigin = fogbugzBaseUrl ? normalizeBase(fogbugzBaseUrl) : null;

  // Parse into a DOM fragment so we manipulate URLs safely (no regex)
  const template = document.createElement("template");
  template.innerHTML = clean;

  // Rewrite <a href> to absolute FogBugz URLs (links open directly, no auth needed)
  if (baseOrigin) {
    for (const a of template.content.querySelectorAll("a")) {
      const raw = a.getAttribute("href");
      if (!raw) continue;
      const resolved = resolveUrl(raw, baseOrigin);
      if (resolved) {
        a.setAttribute("href", resolved);
      } else {
        a.removeAttribute("href");
      }
    }
  }

  // Rewrite <img src> to proxy URLs for FogBugz-origin images; strip the rest
  for (const img of Array.from(template.content.querySelectorAll("img"))) {
    const raw = img.getAttribute("src");
    if (!raw || !baseOrigin) {
      img.remove();
      continue;
    }
    const resolved = resolveUrl(raw, baseOrigin);
    if (!resolved) {
      img.remove();
      continue;
    }
    // Only allow images that live on the FogBugz origin
    try {
      const parsedUrl = new URL(resolved);
      const parsedBase = new URL(baseOrigin);
      if (parsedUrl.origin !== parsedBase.origin) {
        img.remove();
        continue;
      }
    } catch {
      img.remove();
      continue;
    }
    const proxyUrl = buildAttachmentProxyUrl(resolved, { inline: true });
    if (!proxyUrl) {
      img.remove();
      continue;
    }
    const altText = img.getAttribute("alt")?.trim();
    const ariaLabel = altText ? `Open image: ${altText}` : "Open image";
    // Use data-auth-src instead of src to prevent the browser from making
    // an unauthenticated request. AuthenticatedHtmlContent will fetch with
    // Bearer auth and set the real src from a blob URL.
    img.removeAttribute("src");
    img.setAttribute("data-auth-src", proxyUrl);
    img.setAttribute("data-fogbugz-image", "1");
    img.setAttribute("class", "max-w-80 h-auto cursor-zoom-in bg-muted animate-pulse");
    img.setAttribute("tabindex", "0");
    img.setAttribute("role", "button");
    img.setAttribute("aria-label", ariaLabel);
  }

  // Open links in new tabs
  for (const a of template.content.querySelectorAll("a")) {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  }

  return template.innerHTML || null;
}

/**
 * Strip trailing slashes and validate that `base` is a real http(s) URL.
 */
function normalizeBase(base: string): string | null {
  try {
    const url = new URL(base);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return url.origin; // e.g. "https://fogbugz.example.com"
  } catch {
    return null;
  }
}

/**
 * Resolve a potentially relative URL against the FogBugz origin.
 * Returns the absolute URL string if safe (http/https), `null` otherwise.
 */
function resolveUrl(raw: string, baseOrigin: string): string | null {
  try {
    // Already absolute?
    const absolute = new URL(raw, baseOrigin);
    if (absolute.protocol === "http:" || absolute.protocol === "https:") {
      return absolute.toString();
    }
  } catch {
    // unparseable
  }
  return null;
}
